import { testId } from '@tableau/widgets';
import * as React from 'react';
import { ColumnHeadersContext, DataGrid } from './DataGrid';
import { DataGridColors } from './DataGridColors';
import { DefaultFormatter } from './DefaultFormatter';
import { measureWidth } from './Utils';

export class BodyCell extends React.PureComponent<BodyCell.Props> {

  public static displayName: string = 'DataGridBodyCell';

  public componentDidMount(): void {
    if (this.props.reportWidthFn) {
      this.props.reportWidthFn(this.props.col.name, measureWidth(this));
    }
  }

  private getCell(): DataGrid.Cell {
    return { colIdx: this.props.colIdx, rowIdx: this.props.rowIdx, colKey: this.props.col.dataKey, rowKey: this.props.row.key }
  }

  private handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (this.props.onCellClick) {
      const cell = this.getCell();
      this.props.onCellClick(this.props.row.data, cell, e);
    }
  }

  private handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (this.props.onCellDoubleClick) {
      const cell = this.getCell();
      this.props.onCellDoubleClick(this.props.row.data, cell, e);
    }
  }

  private handleContextMenu = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (this.props.onCellContextMenu) {
      const cell = this.getCell();
      this.props.onCellContextMenu(this.props.row.data, cell, e);
    }
  }

  public render(): JSX.Element {
    // tslint:disable:deprecation
    const children = this.props.col.cellComponent ? <this.props.col.cellComponent {...this.props} /> :
      this.props.col.cellFormatter?.(this.props) ??
      <DefaultFormatter value={this.props.value} testId={testId(this.props.rowIdx + '-' + this.props.colIdx, 'Cell')} />;
    // tslint:enable:deprecation

    return <ColumnHeadersContext.Consumer>{showColumnHeaders =>
      <div
        aria-colindex={this.props.colIdx + 1} // aria-colindex counts from 1
        aria-rowindex={this.props.rowIdx + (showColumnHeaders ? 2 : 1)} // aria-rowindex counts from 1
        aria-selected={this.props.isSelected}
        children={children}
        className='tb-react-dg-bcell'
        data-tb-test-id={this.props.col.name + BodyCell.CellTestIdSuffix}
        onContextMenu={this.handleContextMenu}
        onClick={this.handleClick}
        onDoubleClick={this.handleDoubleClick}
        style={BodyCell.getStyle(this.props.isSelected, this.props.height, this.props.width, this.props.showVerticalGridLines)}
        tabIndex={-1}
        role='gridcell'
      />
    }</ColumnHeadersContext.Consumer>;
  }

}

export namespace BodyCell {

  export function getStyle(isSelected: boolean, height?: number, width?: number, showVerticalGridLines?: boolean): React.CSSProperties {
    const borderBottomColor = isSelected ? DataGridColors.SelectedGridLine : 'transparent';
    const borderRightColor = showVerticalGridLines ? (isSelected ? DataGridColors.SelectedGridLine : DataGridColors.VerticalGridLine) : 'transparent';
    return {
      backgroundColor: isSelected ? DataGridColors.Selected : undefined,
      borderBottom: `1px solid ${borderBottomColor}`,
      borderRight: `1px solid ${borderRightColor}`,
      boxSizing: 'border-box',
      display: 'inline-block',
      height: height,
      lineHeight: height + 'px',
      verticalAlign: 'top',
      width: width
    };
  };

  export const CellTestIdSuffix = '-cell';

  export interface Props {
    width?: number;
    col: DataGrid.Column;
    row: DataGrid.Row;
    colIdx: number;
    rowIdx: number;

    // tslint:disable-next-line:no-any
    value: any;
    isSelected: boolean;

    /** @deprecated please remove all references to this prop */
    onCellFocus?: Function;
    /** @deprecated please remove all references to this prop */
    isFocused?: boolean;
    /** @deprecated please remove all references to this prop */
    isTabbable?: boolean;

    /**
     * Optional callback for reporting back cell width.
     */
    reportWidthFn?: DataGrid.ReportWidthFn;

    /**
     * Optional callback for cell clicks.
     */
    onCellClick?: DataGrid.ClickHandler;

    /**
     * Optional callback for cell double click.
     */
    onCellDoubleClick?: DataGrid.ClickHandler;

    /**
     * Optional callback for cell context menu events (aka right clck).
     */
    onCellContextMenu?: DataGrid.ClickHandler;

    height: number;

    showVerticalGridLines?: boolean;
  }
}
